// Slot-local tier table (T7, D7). Tier before everything in the ranker
// (G14), so bucket→tier IS the ordering policy:
//   T1 compatible columns (FK partner: orderHint 0 within tier)
//   T2 neutral columns (by direction) + qualifier items
//   T3 subquery/quantified snippets, TRUE/FALSE, compatible-return routines
//   T4 lossy columns + unknown-return routines
//   T5 incompatible columns
// An UNRESOLVED LHS ('unknown'/'null') gives no demotion signal — every
// column stays T1 (the plan's "both T1 on unresolved LHS" repro), distinct
// from direction-neutral pairs which sit at T2.

import type { CompletionCandidateTier, DatabaseEngineId } from '@/core/engines';
import type { OperandLiteralSqlType } from '@/features/sql-resolver/types/queryStructure';
import { compatBucket, type CompatBucket } from './compat';

export const QUALIFIER_TIER: CompletionCandidateTier = 2;
export const KEYWORD_TIER: CompletionCandidateTier = 3;
/** Enum values at a resolved value target (T18a) — the top of the list:
 *  a positively-typed target column's declared enum members are the most
 *  likely intended values. */
export const ENUM_VALUE_TIER: CompletionCandidateTier = 1;
/** Engine-owned extra scalar sources are clamped to ≥ this tier. */
export const EXTRA_SOURCE_MIN_TIER: CompletionCandidateTier = 3;
/** FK partner of the LHS ranks first within its tier (D7). */
export const FK_PARTNER_ORDER_HINT = 0;

export function lhsIsResolved(lhsType: OperandLiteralSqlType): boolean {
  return lhsType !== 'unknown' && lhsType !== 'null';
}

export function tierForColumn(
  bucket: CompatBucket,
  lhsResolved: boolean,
): CompletionCandidateTier {
  if (!lhsResolved) return 1;
  switch (bucket) {
    case 'compatible':
      return 1;
    case 'neutral':
      return 2;
    case 'lossy':
      return 4;
    case 'incompatible':
      return 5;
  }
}

export function tierForRoutine(
  bucket: CompatBucket,
  lhsResolved: boolean,
): CompletionCandidateTier {
  // Compatible-return routines T3; neutral/unknown + lossy T4; provably
  // incompatible T5 (mirrors the column T5). Unresolved LHS gives no
  // demotion signal — flat T4 (D7, amended per spec §A3).
  if (!lhsResolved) return 4;
  switch (bucket) {
    case 'compatible':
      return 3;
    case 'neutral':
    case 'lossy':
      return 4;
    case 'incompatible':
      return 5;
  }
}

/** Slot-facing routine tier: compat tier + one-tier demotion for
 *  `_`-prefixed internals (spec §A3 — "bottom of tier" is not expressible
 *  because orderHint defaults to MAX for every candidate). */
export function routineCandidateTier(
  engine: DatabaseEngineId,
  lhsType: OperandLiteralSqlType,
  lhsResolved: boolean,
  d: { readonly label: string; readonly returnsCoarse: OperandLiteralSqlType },
): CompletionCandidateTier {
  const base = tierForRoutine(compatBucket(engine, lhsType, d.returnsCoarse), lhsResolved);
  if (!d.label.startsWith('_')) return base;
  return Math.min(base + 1, 8) as CompletionCandidateTier;
}
